// scrabble-board.js — Alpine x-data factory for the Scrabble product page; loaded synchronously (see the section) so it exists before Alpine boots.
function generateUUID() {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c) {
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    },
  );
  return uuid;
}

function scrabbleApp() {
  return {
    names: ['', ''],
    canvas: null,
    ctx: null,
    tileSize: 40,
    board: [],
    boardWidth: 20,
    boardHeight: 15,
    isValid: false,
    showAddNameButton: true,
    placeholderImage: null,

    init() {
      this.canvas = document.getElementById('scrabbleCanvas');
      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();
      window.addEventListener('resize', this.resizeCanvas.bind(this));
      this.board = Array(this.boardHeight)
        .fill()
        .map(() => Array(this.boardWidth).fill(''));
      this.checkInputs();
      this.updateAddNameButtonVisibility();
      this.createPlaceholderImage();
    },

    createPlaceholderImage() {
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = this.canvas.width;
      tempCanvas.height = this.canvas.height;

      // Set background
      tempCtx.fillStyle = '#f0f0f0';
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      // Draw text
      tempCtx.font = 'bold 32px Arial';
      tempCtx.fillStyle = '#333333';
      tempCtx.textAlign = 'center';
      tempCtx.textBaseline = 'middle';

      tempCtx.fillText(
        'Enter names to begin...',
        tempCanvas.width / 2,
        (tempCanvas.height * 2) / 5,
      );

      // Create image from canvas
      this.placeholderImage = new Image();
      this.placeholderImage.src = tempCanvas.toDataURL();
      this.placeholderImage.onload = () => this.renderBoard();
    },

    resizeCanvas() {
      const container = this.canvas.parentElement;
      const containerWidth = container.clientWidth;
      const maxWidth = 800; // Maximum canvas width
      const aspectRatio = this.boardHeight / this.boardWidth;

      let canvasWidth = Math.min(containerWidth, maxWidth);
      let canvasHeight = canvasWidth * aspectRatio;

      this.canvas.width = canvasWidth;
      this.canvas.height = canvasHeight;

      this.tileSize = canvasWidth / this.boardWidth;

      this.renderBoard();
    },

    addNameField() {
      if (this.names.length < 4) {
        this.names.push('');
        this.checkInputs();
        this.updateAddNameButtonVisibility();
      }
    },

    updateAddNameButtonVisibility() {
      this.showAddNameButton = this.names.length < 4;
    },

    generateBoard() {
      const image = document.getElementById('defaultScrabbleImage');
      image.classList.add('hidden');
      const board = document.getElementById('scrabbleCanvas');
      board.classList.remove('hidden');
      const validNames = this.names.filter((name) => name.trim() !== '');
      this.drawScrabbleBoard(validNames);
      this.updateScrabbleBoardInput();
      this.checkInputs();
      const randomString = generateUUID();
      document.getElementById('scrabble_board_image_id').value = randomString;
      document.getElementById('layout-message').classList.remove('hidden');
    },

    rearrangeNames() {
      const validNames = this.names.filter((name) => name.trim() !== '');
      for (let i = validNames.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [validNames[i], validNames[j]] = [validNames[j], validNames[i]];
      }
      this.drawScrabbleBoard(validNames);
      this.updateScrabbleBoardInput();
      this.checkInputs();
    },

    clearCanvas() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.board = Array(this.boardHeight)
        .fill()
        .map(() => Array(this.boardWidth).fill(''));
    },

    drawScrabbleBoard(names) {
      this.clearCanvas();

      if (names.length === 0) {
        this.renderBoard();
        return;
      }

      this.canvas.setAttribute(
        'aria-label',
        `Scrabble board preview with the names: ${names.join(', ')}`,
      );

      // Place the first name in the center horizontally
      this.placeWord(
        names[0].toUpperCase(),
        Math.floor(this.boardWidth / 2) - Math.floor(names[0].length / 2),
        Math.floor(this.boardHeight / 2),
        true,
      );

      // Place subsequent names
      for (let i = 1; i < names.length; i++) {
        const name = names[i].toUpperCase();
        let placed = false;

        // Try to connect with existing words
        for (let y = 0; y < this.boardHeight && !placed; y++) {
          for (let x = 0; x < this.boardWidth && !placed; x++) {
            if (this.board[y][x] !== '' && name.includes(this.board[y][x])) {
              const index = name.indexOf(this.board[y][x]);
              if (this.canPlaceWord(name, x - index, y, true, index)) {
                this.placeWord(name, x - index, y, true);
                placed = true;
              } else if (this.canPlaceWord(name, x, y - index, false, index)) {
                this.placeWord(name, x, y - index, false);
                placed = true;
              }
            }
          }
        }

        // If can't connect, place below the last word
        if (!placed) {
          let y = this.boardHeight - 1;
          while (y >= 0 && this.board[y].every((cell) => cell === '')) y--;
          y += 2;
          const x =
            Math.floor(this.boardWidth / 2) - Math.floor(name.length / 2);
          if (this.canPlaceWord(name, x, y, true, -1)) {
            this.placeWord(name, x, y, true);
          }
        }
      }

      this.renderBoard();
    },

    canPlaceWord(word, x, y, horizontal, intersectIndex) {
      if (horizontal) {
        if (x < 0 || x + word.length > this.boardWidth) return false;
        for (let i = 0; i < word.length; i++) {
          if (i !== intersectIndex) {
            if (this.board[y][x + i] !== '') return false;
            if (y > 0 && this.board[y - 1][x + i] !== '') return false;
            if (y < this.boardHeight - 1 && this.board[y + 1][x + i] !== '')
              return false;
          }
          if (i === 0 && x > 0 && this.board[y][x - 1] !== '') return false;
          if (
            i === word.length - 1 &&
            x + i < this.boardWidth - 1 &&
            this.board[y][x + i + 1] !== ''
          )
            return false;
        }
      } else {
        if (y < 0 || y + word.length > this.boardHeight) return false;
        for (let i = 0; i < word.length; i++) {
          if (i !== intersectIndex) {
            if (this.board[y + i][x] !== '') return false;
            if (x > 0 && this.board[y + i][x - 1] !== '') return false;
            if (x < this.boardWidth - 1 && this.board[y + i][x + 1] !== '')
              return false;
          }
          if (i === 0 && y > 0 && this.board[y - 1][x] !== '') return false;
          if (
            i === word.length - 1 &&
            y + i < this.boardHeight - 1 &&
            this.board[y + i + 1][x] !== ''
          )
            return false;
        }
      }
      return true;
    },

    placeWord(word, x, y, horizontal) {
      if (horizontal) {
        for (let i = 0; i < word.length; i++) {
          this.board[y][x + i] = word[i];
        }
      } else {
        for (let i = 0; i < word.length; i++) {
          this.board[y + i][x] = word[i];
        }
      }
    },

    renderBoard() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // Draw placeholder image if no words are placed
      if (this.board.every((row) => row.every((cell) => cell === ''))) {
        if (this.placeholderImage) {
          this.ctx.drawImage(
            this.placeholderImage,
            0,
            0,
            this.canvas.width,
            this.canvas.height,
          );
        }
        return;
      }

      this.ctx.font = `${this.tileSize * 0.5}px Arial`;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';

      for (let y = 0; y < this.boardHeight; y++) {
        for (let x = 0; x < this.boardWidth; x++) {
          if (this.board[y][x] !== '') {
            this.ctx.fillStyle = 'beige';
            this.ctx.fillRect(
              x * this.tileSize,
              y * this.tileSize,
              this.tileSize,
              this.tileSize,
            );
            this.ctx.strokeRect(
              x * this.tileSize,
              y * this.tileSize,
              this.tileSize,
              this.tileSize,
            );
            this.ctx.fillStyle = 'black';
            this.ctx.fillText(
              this.board[y][x],
              x * this.tileSize + this.tileSize / 2,
              y * this.tileSize + this.tileSize / 2,
            );
          }
        }
      }
    },

    updateScrabbleBoardInput() {
      const imageData = this.canvas.toDataURL('image/jpeg');
      const scrabbleBoardInput = document.getElementById('scrabble_board');
      if (scrabbleBoardInput) {
        scrabbleBoardInput.value = imageData;
      }
      this.checkInputs();
    },

    checkInputs() {
      const validNames = this.names.filter((name) => name.trim() !== '');
      const scrabbleBoardInput = document.getElementById('scrabble_board');
      this.isValid =
        validNames.length > 0 &&
        scrabbleBoardInput &&
        scrabbleBoardInput.value !== '';
    },
  };
}
